import { Flex, useDisclosure } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useDeleteTRSNode from './queries/useDeleteTRSNode';
import useGetTRSNode from './queries/useGetTRSNode';
import useUpdateTRSNode from './queries/useUpdateTRSNode';

import FormBase from '@/components/forms/new/FormBase';
import { Button, LoadingIndicator } from 'components/v4';
import {
    breadCrumbTrailEdit as breadCrumbTrail,
    editHeader as header,
    trsNodesPath,
    ERROR_MESSAGES,
} from './constants';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import TRSNodeNameField from './formFields/TRSNodeNameField';
import TRSNodeCapacityField from './formFields/TRSNodeCapacityField';
import { useLocation } from 'react-router-dom';

const TRSNodesEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { clusterID = '' } = useLocation()?.state || {};
    const { darkMode } = useSelector((state) => state.settings);
    const { restError } = useSelector((state) => state.modal);

    const [isDisabled, setIsDisabled] = useState(true);
    const [currentNode, setCurrentNode] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const statefulBreadCrumbTrail = breadCrumbTrail.map(
        ({ label, path: prevPath }) => ({
            label,
            path: [prevPath, { index: prevPath ? 0 : null, clusterID }],
        }),
    );

    const {
        data: nodeData,
        isLoading: nodeLoading,
        error: nodeError,
        isFetching: nodeFetching,
    } = useGetTRSNode(id);

    useEffect(() => {
        if (nodeData) {
            setCurrentNode(nodeData);
        }
    }, [nodeData]);

    const handleUpdateSuccess = () => {
        toast.success('TRS Node updated successfully!');
        navigate(trsNodesPath, { state: { clusterID } });
    };

    const handleUpdateError = (error) => {
        console.error('Failed to update TRS Node:', error);
        toast.error('Failed to update TRS Node. Please try again.');
    };

    const handleDeleteSuccess = () => {
        toast.success('TRS Node deleted successfully!');
        navigate(trsNodesPath, { state: { clusterID } });
        onClose();
    };

    const handleDeleteError = (error) => {
        console.error('Failed to delete TRS Node:', error);
        toast.error(
            restError ?? 'Failed to delete TRS Node. Please try again.',
        );
    };

    const { mutate: updateNode, isPending: updatePending } = useUpdateTRSNode(
        handleUpdateSuccess,
        handleUpdateError,
    );

    const { mutate: deleteNode, isPending: deletePending } = useDeleteTRSNode(
        handleDeleteSuccess,
        handleDeleteError,
    );

    const finalFormSubmit = async (values) => {
        // Basic validation
        if (!values.name || values.name.length < 2) {
            console.error(ERROR_MESSAGES.NODE_NAME_REQUIRED);
            return;
        }

        if (!values.capacity || values.capacity <= 0) {
            console.error(ERROR_MESSAGES.CAPACITY_INVALID);
            return;
        }

        const finalSubmit = {
            id: id,
            name: values.name,
            capacity: parseInt(values.capacity, 10),
            clusterID: currentNode.clusterID, // Keep existing cluster
        };

        updateNode(finalSubmit);
    };

    const handleDeleteConfirm = () => {
        deleteNode(id);
    };

    const formFields = (errors, touched, values) => {
        const fields = [
            <TRSNodeNameField
                key="name"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <TRSNodeCapacityField
                key="capacity"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
        ];

        // Enable save button when required fields are valid
        const hasValidFields =
            values?.name &&
            values?.capacity &&
            parseInt(values.capacity, 10) > 0;

        if (hasValidFields) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }

        return fields;
    };

    const extraButton = (
        <Flex justify="flex-end" width="100%">
            <Button
                w={142}
                colorScheme="red"
                onClick={onOpen}
                isDisabled={deletePending || updatePending}>
                Delete
            </Button>
        </Flex>
    );

    const isFetchingData = nodeLoading || nodeFetching || updatePending;

    // Handle error states
    if (nodeError) {
        toast.error('Failed to load node data. Redirecting to nodes list.');
        navigate(trsNodesPath, { state: { clusterID } });
        return <LoadingIndicator />;
    }

    // Show loading while data is being fetched
    if (isFetchingData || !currentNode) {
        return <LoadingIndicator />;
    }

    // Prepare initial values with fetched node data
    const editInitialValues = {
        name: currentNode.name || '',
        capacity: currentNode.capacity || '',
    };

    return (
        <>
            <FormBase
                isDisabled={isDisabled}
                breadCrumbTrail={statefulBreadCrumbTrail}
                header={header}
                initialValues={editInitialValues}
                finalFormSubmit={finalFormSubmit}
                backLink={[trsNodesPath, { state: { index: 0, clusterID } }]}
                extraButton={extraButton}>
                {({ errors, touched, values }) =>
                    formFields(errors, touched, values)
                }
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                nodeName={currentNode?.name}
                onConfirm={handleDeleteConfirm}
                isDeleting={deletePending}
            />
        </>
    );
};

export default TRSNodesEdit;
